export const GPS_GAP_MS = 15_000;

export interface GpsSample {
  lat: number;
  lon: number;
  speed_kmh: number | null;
  heading_deg: number | null;
  recorded_at: string;
}

export interface GpsAtTime {
  lat: number;
  lon: number;
  speed_kmh: number | null;
  heading_deg: number | null;
  recorded_at: string;
  gap: boolean;
  interpolated: boolean;
}

function sampleTime(row: GpsSample): number {
  return Date.parse(row.recorded_at);
}

/** Last point at or before timeMs, plus optional interpolation across a short span. */
export function positionAtTime(points: GpsSample[], timeMs: number, gapMs = GPS_GAP_MS): GpsAtTime | null {
  if (points.length === 0 || !Number.isFinite(timeMs)) return null;
  let lo = 0;
  let hi = points.length - 1;
  let idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = sampleTime(points[mid]!);
    if (t <= timeMs) {
      idx = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (idx < 0) {
    const first = points[0]!;
    return {
      lat: first.lat,
      lon: first.lon,
      speed_kmh: first.speed_kmh,
      heading_deg: first.heading_deg,
      recorded_at: first.recorded_at,
      gap: sampleTime(first) - timeMs > gapMs,
      interpolated: false,
    };
  }
  const cur = points[idx]!;
  const curT = sampleTime(cur);
  const next = points[idx + 1];
  if (next) {
    const nextT = sampleTime(next);
    const span = nextT - curT;
    if (span > 0 && span <= gapMs && timeMs >= curT && timeMs <= nextT) {
      const t = (timeMs - curT) / span;
      return {
        lat: cur.lat + (next.lat - cur.lat) * t,
        lon: cur.lon + (next.lon - cur.lon) * t,
        speed_kmh: next.speed_kmh ?? cur.speed_kmh,
        heading_deg: next.heading_deg ?? cur.heading_deg,
        recorded_at: new Date(timeMs).toISOString(),
        gap: false,
        interpolated: true,
      };
    }
  }
  const gap = timeMs - curT > gapMs;
  return {
    lat: cur.lat,
    lon: cur.lon,
    speed_kmh: cur.speed_kmh,
    heading_deg: cur.heading_deg,
    recorded_at: cur.recorded_at,
    gap,
    interpolated: false,
  };
}

export function trailUpTo(points: GpsSample[], timeMs: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const row of points) {
    if (sampleTime(row) > timeMs) break;
    out.push([row.lat, row.lon]);
  }
  return out;
}
